import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  FaArrowLeft,
  FaBolt,
  FaBroadcastTower,
  FaCheckCircle,
  FaClock,
  FaEdit,
  FaExclamationTriangle,
  FaMapMarkerAlt,
  FaPause,
  FaPlay,
  FaStepBackward,
  FaStepForward,
  FaStore,
  FaSyncAlt,
  FaTv,
} from "react-icons/fa";
import ConfirmModal from "../../components/ConfirmModal";
import TvEmptyState from "../../components/tv-disply/TvEmptyState";
import TvPresentationPreviewCanvas from "../../components/tv-disply/TvPresentationPreviewCanvas";
import {
  DEFAULT_IMAGE_DURATION_SECONDS,
  TV_DEVICE_STATUSES,
  TV_MEDIA_TYPES,
  TV_PAIRING_STATUSES,
  TV_PUBLISH_TARGET_MODES,
} from "../../services/tv-disply/tvDisplayConstants";
import {
  subscribeToTvPlaylistItems,
  updatePlaylistItemOverlay,
} from "../../services/tv-disply/tvPlaylistService";
import {
  getComputedDeliveryStatus,
  publishTvPlaylist,
  validatePlaylistForPublish,
} from "../../services/tv-disply/tvPublishService";

const EMPTY_OVERLAY = {
  enabled: false,
  badge: "",
  headline: "",
  subheadline: "",
  priceText: "",
  ctaText: "",
  position: "BOTTOM_LEFT",
  textAlign: "LEFT",
  textColor: "#FFFFFF",
  accentColor: "#FF4F12",
  backgroundColor: "#000000",
  backgroundOpacity: 0.55,
  animation: "FADE_UP",
};

export default function TvOutlets({ deviceRows = [], playlistRows = [], mediaRows = [], loading = false }) {
  const [selectedOutletKey, setSelectedOutletKey] = useState("");
  const outlets = useMemo(() => groupDevicesByOutlet(deviceRows), [deviceRows]);
  const selectedOutlet = outlets.find((outlet) => outlet.key === selectedOutletKey) || null;

  useEffect(() => {
    if (selectedOutletKey && !outlets.some((outlet) => outlet.key === selectedOutletKey)) {
      setSelectedOutletKey("");
    }
  }, [outlets, selectedOutletKey]);

  if (selectedOutlet) {
    return (
      <OutletCommandCenter
        outlet={selectedOutlet}
        playlistRows={playlistRows}
        mediaRows={mediaRows}
        onBack={() => setSelectedOutletKey("")}
      />
    );
  }

  return (
    <section className="tv-outlets-page">
      <div className="tv-outlets-hero">
        <span className="tv-outlets-eyebrow"><FaStore /> Your live network</span>
        <h2>All Outlets</h2>
      </div>

      {loading ? <div className="tv-display-feedback is-loading">Syncing outlets and TV activity.</div> : null}

      {!loading && !outlets.length ? (
        <div className="tv-display-panel">
          <TvEmptyState title="No outlets yet" copy="Pair a TV and add its city/location to create the first outlet card." />
        </div>
      ) : (
        <div className="tv-outlet-card-grid">
          {outlets.map((outlet) => (
            <button key={outlet.key} type="button" className="tv-outlet-card" onClick={() => setSelectedOutletKey(outlet.key)}>
              <div className="tv-outlet-card-top">
                <span className="tv-outlet-card-icon"><FaStore /></span>
                <span className={`tv-outlet-live-pill${outlet.onlineCount ? " is-online" : " is-offline"}`}>
                  <span /> {outlet.onlineCount ? "LIVE" : "OFFLINE"}
                </span>
              </div>
              <div className="tv-outlet-card-body">
                <h3>{outlet.name}</h3>
                <p><FaMapMarkerAlt /> {outlet.location}</p>
              </div>
              <div className="tv-outlet-card-metrics">
                <span><strong>{outlet.devices.length}</strong><small>TVs</small></span>
                <span><strong className="is-green">{outlet.onlineCount}</strong><small>Online</small></span>
                <span><strong className={outlet.offlineCount ? "is-red" : ""}>{outlet.offlineCount}</strong><small>Offline</small></span>
              </div>
              <div className="tv-outlet-card-playlist">
                <span><FaPlay /></span>
                <span><small>Now assigned</small><strong>{outlet.playlistNames.join(", ") || "No playlist assigned"}</strong></span>
              </div>
              <div className="tv-outlet-card-footer">
                <span>{outlet.syncedCount === outlet.devices.length && outlet.devices.length ? <FaCheckCircle /> : <FaSyncAlt />} {outlet.syncedCount}/{outlet.devices.length} synced</span>
                <strong>Open Outlet →</strong>
              </div>
            </button>
          ))}
        </div>
      )}
    </section>
  );
}

function OutletCommandCenter({ outlet, playlistRows, mediaRows, onBack }) {
  const [selectedPlaylistId, setSelectedPlaylistId] = useState("");
  const [playlistItems, setPlaylistItems] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [itemsError, setItemsError] = useState("");
  const [previewIndex, setPreviewIndex] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [overlayDraft, setOverlayDraft] = useState(EMPTY_OVERLAY);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [actionError, setActionError] = useState("");
  const videoRef = useRef(null);

  const assignedPlaylistId = useMemo(() => getPrimaryAssignedPlaylistId(outlet.devices), [outlet.devices]);

  useEffect(() => {
    const preferred = assignedPlaylistId && playlistRows.some((row) => row.id === assignedPlaylistId)
      ? assignedPlaylistId
      : playlistRows[0]?.id || "";
    setSelectedPlaylistId(preferred);
  }, [assignedPlaylistId, outlet.key, playlistRows]);

  useEffect(() => {
    setPreviewIndex(0);
    setPlaying(false);
    if (!selectedPlaylistId) {
      setPlaylistItems([]);
      return undefined;
    }
    setItemsLoading(true);
    return subscribeToTvPlaylistItems(
      selectedPlaylistId,
      (rows) => {
        setPlaylistItems(rows);
        setItemsLoading(false);
        setItemsError("");
      },
      (error) => {
        setItemsLoading(false);
        setItemsError(error?.message || "Unable to load this playlist.");
      }
    );
  }, [selectedPlaylistId]);

  const selectedPlaylist = playlistRows.find((row) => row.id === selectedPlaylistId) || null;
  const mediaMap = useMemo(() => new Map(mediaRows.map((media) => [media.id, media])), [mediaRows]);
  const previewItems = useMemo(
    () => playlistItems.map((item) => ({ ...item, media: mediaMap.get(item.mediaId) || null })).filter((item) => item.enabled !== false && item.media?.downloadURL),
    [mediaMap, playlistItems]
  );
  const currentItem = previewItems[previewIndex] || null;
  const previewItem = currentItem ? { ...currentItem, overlay: overlayDraft } : null;
  const eligibleDeviceIds = outlet.devices
    .filter((device) => device.enabled !== false && device.pairingStatus === TV_PAIRING_STATUSES.PAIRED)
    .map((device) => device.deviceId || device.id);

  useEffect(() => {
    setOverlayDraft(currentItem?.overlay ? { ...EMPTY_OVERLAY, ...currentItem.overlay } : EMPTY_OVERLAY);
  }, [currentItem?.id, currentItem?.overlay]);

  useEffect(() => {
    if (previewIndex >= previewItems.length) setPreviewIndex(0);
  }, [previewIndex, previewItems.length]);

  useEffect(() => {
    if (!playing || !currentItem || currentItem.media?.type !== TV_MEDIA_TYPES.IMAGE) return undefined;
    const seconds = currentItem.imageDurationSeconds || selectedPlaylist?.presentation?.defaultImageDurationSeconds || DEFAULT_IMAGE_DURATION_SECONDS;
    const timer = window.setTimeout(() => setPreviewIndex((value) => nextIndex(value, previewItems.length)), Math.max(1, seconds) * 1000);
    return () => window.clearTimeout(timer);
  }, [currentItem, playing, previewItems.length, selectedPlaylist?.presentation?.defaultImageDurationSeconds]);

  async function saveOverlay() {
    if (!currentItem || !selectedPlaylistId) return;
    setSaving(true);
    setActionError("");
    setFeedback("");
    try {
      await updatePlaylistItemOverlay(selectedPlaylistId, currentItem.id, overlayDraft);
      setFeedback("Live preview content saved as a draft. Publish when it looks perfect.");
    } catch (error) {
      setActionError(error?.message || "Unable to save the overlay.");
    } finally {
      setSaving(false);
    }
  }

  async function preparePublish() {
    setActionError("");
    setFeedback("");
    if (!selectedPlaylist || !eligibleDeviceIds.length) {
      setActionError("This outlet has no eligible paired TVs to publish to.");
      return;
    }
    try {
      const validation = await validatePlaylistForPublish({
        playlistId: selectedPlaylist.id,
        targetMode: TV_PUBLISH_TARGET_MODES.SELECTED,
        deviceIds: eligibleDeviceIds,
      });
      if (validation.ready === false) {
        const issues = [
          ...(validation.blockingIssues || []),
          ...(validation.errors || []),
          ...(validation.issues || []),
        ];
        setActionError(issues.map((issue) => issue.message || issue).join(" ") || "This playlist is not ready to publish.");
        return;
      }
      setPublishOpen(true);
    } catch (error) {
      setActionError(error?.message || "Unable to validate this playlist.");
    }
  }

  async function publishOutlet() {
    setPublishing(true);
    setActionError("");
    try {
      const result = await publishTvPlaylist({
        playlistId: selectedPlaylist.id,
        targetMode: TV_PUBLISH_TARGET_MODES.SELECTED,
        deviceIds: eligibleDeviceIds,
        requestId: `outlet_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      });
      setPublishOpen(false);
      setFeedback(`Version ${result.version || "new"} published to ${result.successfulTargetCount ?? eligibleDeviceIds.length} TV(s) at ${outlet.name}.`);
    } catch (error) {
      setActionError(error?.message || "Outlet publication failed.");
    } finally {
      setPublishing(false);
    }
  }

  return (
    <section className="tv-outlet-command">
      <button type="button" className="tv-outlet-back" onClick={onBack}><FaArrowLeft /> All Outlets</button>

      <div className="tv-outlet-command-hero">
        <div>
          <span className="tv-outlets-eyebrow"><FaBolt /> Live outlet studio</span>
          <h2>{outlet.name}</h2>
          <p><FaMapMarkerAlt /> {outlet.location}</p>
        </div>
        <div className="tv-outlet-command-health">
          <span className="is-green"><strong>{outlet.onlineCount}</strong> online</span>
          <span className={outlet.offlineCount ? "is-red" : ""}><strong>{outlet.offlineCount}</strong> offline</span>
          <span><strong>{outlet.syncedCount}</strong> synced</span>
        </div>
      </div>

      <div className="tv-outlet-screen-strip">
        {outlet.devices.map((device) => {
          const online = device.computedStatus === TV_DEVICE_STATUSES.ONLINE;
          return (
            <div key={device.id} className={`tv-outlet-screen-chip${online ? " is-online" : " is-offline"}`}>
              <FaTv />
              <span><strong>{device.deviceName || "TV Screen"}</strong><small>{online ? "Connected now" : formatLastSeen(device.lastSeenAt)}</small></span>
              <span className="tv-outlet-screen-dot" />
            </div>
          );
        })}
      </div>

      {feedback ? <div className="tv-display-feedback is-success"><FaCheckCircle /> {feedback}</div> : null}
      {actionError || itemsError ? <div className="tv-display-feedback is-error"><FaExclamationTriangle /> {actionError || itemsError}</div> : null}

      <div className="tv-outlet-studio-grid">
        <div className="tv-outlet-preview-panel">
          <div className="tv-outlet-studio-head">
            <div><span>REAL CONTENT PREVIEW</span><h3>What customers will see</h3></div>
            <span className="tv-outlet-draft-badge">DRAFT PREVIEW</span>
          </div>

          <label className="tv-display-field">
            <span className="tv-display-field-label">Assigned playlist</span>
            <select className="tv-media-select" value={selectedPlaylistId} onChange={(event) => setSelectedPlaylistId(event.target.value)}>
              <option value="">Choose a playlist</option>
              {playlistRows.map((playlist) => <option key={playlist.id} value={playlist.id}>{playlist.name} · {playlist.itemCount} items</option>)}
            </select>
          </label>

          <div className="tv-outlet-preview-stage">
            {itemsLoading ? (
              <div className="tv-display-feedback is-loading">Loading real playlist content.</div>
            ) : previewItem ? (
              <TvPresentationPreviewCanvas
                key={`${selectedPlaylistId}_${currentItem.id}`}
                playlist={selectedPlaylist}
                item={previewItem}
                muted
                playing={playing}
                autoPlayVideo={playing}
                videoRef={videoRef}
                onVideoEnded={() => setPreviewIndex((value) => nextIndex(value, previewItems.length))}
                showNativeVideoControls={false}
              />
            ) : (
              <TvEmptyState title="No playable content" copy="Choose a playlist with active Media Library items." />
            )}
          </div>

          <div className="tv-outlet-player-bar">
            <button type="button" onClick={() => setPreviewIndex((value) => previousIndex(value, previewItems.length))} disabled={!previewItems.length}><FaStepBackward /></button>
            <button type="button" className="is-play" onClick={() => setPlaying((value) => !value)} disabled={!previewItems.length}>{playing ? <FaPause /> : <FaPlay />}</button>
            <button type="button" onClick={() => setPreviewIndex((value) => nextIndex(value, previewItems.length))} disabled={!previewItems.length}><FaStepForward /></button>
            <span>{previewItems.length ? `${previewIndex + 1} of ${previewItems.length}` : "0 items"}</span>
            <strong>{currentItem?.media?.name || "Waiting for content"}</strong>
          </div>
        </div>

        <div className="tv-outlet-editor-panel">
          <div className="tv-outlet-studio-head">
            <div><span>LIVE DESIGN CONTROLS</span><h3><FaEdit /> Edit current scene</h3></div>
          </div>
          <p className="tv-outlet-editor-note">Changes appear in the preview instantly. Save the draft, then publish it to this outlet.</p>
          <label className="tv-outlet-switch-row">
            <span><strong>Promotional overlay</strong><small>Show text over this media</small></span>
            <input type="checkbox" checked={overlayDraft.enabled} onChange={(event) => setOverlayDraft((value) => ({ ...value, enabled: event.target.checked }))} disabled={!currentItem} />
          </label>
          <EditorField label="Badge" value={overlayDraft.badge} placeholder="TODAY ONLY" onChange={(badge) => setOverlayDraft((value) => ({ ...value, badge }))} disabled={!currentItem} />
          <EditorField label="Main title" value={overlayDraft.headline} placeholder="Make every moment special" onChange={(headline) => setOverlayDraft((value) => ({ ...value, headline }))} disabled={!currentItem} />
          <EditorField label="Supporting text" value={overlayDraft.subheadline} placeholder="Premium artists, unforgettable celebrations" onChange={(subheadline) => setOverlayDraft((value) => ({ ...value, subheadline }))} disabled={!currentItem} />
          <div className="tv-outlet-editor-split">
            <EditorField label="Price / offer" value={overlayDraft.priceText} placeholder="From ₹4,999" onChange={(priceText) => setOverlayDraft((value) => ({ ...value, priceText }))} disabled={!currentItem} />
            <EditorField label="Action label" value={overlayDraft.ctaText} placeholder="BOOK NOW" onChange={(ctaText) => setOverlayDraft((value) => ({ ...value, ctaText }))} disabled={!currentItem} />
          </div>
          <div className="tv-outlet-style-row">
            <label><span>Brand accent</span><input type="color" value={overlayDraft.accentColor || "#FF4F12"} onChange={(event) => setOverlayDraft((value) => ({ ...value, accentColor: event.target.value }))} disabled={!currentItem} /></label>
            <label><span>Position</span><select value={overlayDraft.position || "BOTTOM_LEFT"} onChange={(event) => setOverlayDraft((value) => ({ ...value, position: event.target.value }))} disabled={!currentItem}><option value="TOP_LEFT">Top left</option><option value="TOP_CENTER">Top center</option><option value="TOP_RIGHT">Top right</option><option value="CENTER_LEFT">Centre left</option><option value="CENTER">Center</option><option value="CENTER_RIGHT">Centre right</option><option value="BOTTOM_LEFT">Bottom left</option><option value="BOTTOM_CENTER">Bottom center</option><option value="BOTTOM_RIGHT">Bottom right</option></select></label>
          </div>
          <button type="button" className="tv-outlet-save-btn" onClick={saveOverlay} disabled={!currentItem || saving}>{saving ? <FaSyncAlt className="is-spinning" /> : <FaEdit />} {saving ? "Saving draft..." : "Save scene changes"}</button>
        </div>
      </div>

      <div className="tv-outlet-publish-dock">
        <div className="tv-outlet-publish-copy"><span><FaBroadcastTower /></span><div><strong>Ready to update {outlet.name}?</strong><small>Creates one protected version and targets only this outlet’s {eligibleDeviceIds.length} paired TV(s).</small></div></div>
        <div className="tv-outlet-publish-meta"><FaClock /> TVs receive it on their next realtime sync</div>
        <button type="button" onClick={preparePublish} disabled={!selectedPlaylist || !eligibleDeviceIds.length || publishing}><FaBolt /> Preview complete — Publish now</button>
      </div>

      <ConfirmModal
        open={publishOpen}
        title={`Publish to ${outlet.name}?`}
        message={`Send “${selectedPlaylist?.name || "this playlist"}” to ${eligibleDeviceIds.length} paired TV(s). This creates a new immutable version and replaces their current assignment.`}
        confirmText="Publish to outlet"
        loading={publishing}
        error={actionError}
        onCancel={() => !publishing && setPublishOpen(false)}
        onConfirm={publishOutlet}
      />
    </section>
  );
}

function EditorField({ label, value, placeholder, onChange, disabled }) {
  return <label className="tv-outlet-editor-field"><span>{label}</span><input value={value || ""} placeholder={placeholder} onChange={(event) => onChange(event.target.value)} disabled={disabled} /></label>;
}

function groupDevicesByOutlet(devices) {
  const groups = new Map();
  devices.forEach((device) => {
    const city = clean(device.city);
    const state = clean(device.state);
    const country = clean(device.country);
    const key = [city || "unassigned", state, country].join("|").toLowerCase();
    if (!groups.has(key)) {
      groups.set(key, { key, name: city || "Unassigned Outlet", location: [state, country].filter(Boolean).join(", ") || "Location not added", devices: [] });
    }
    groups.get(key).devices.push(device);
  });
  return Array.from(groups.values()).map((outlet) => {
    const onlineCount = outlet.devices.filter((device) => device.computedStatus === TV_DEVICE_STATUSES.ONLINE).length;
    const syncedCount = outlet.devices.filter((device) => getComputedDeliveryStatus(device) === "UP_TO_DATE").length;
    return {
      ...outlet,
      onlineCount,
      offlineCount: Math.max(0, outlet.devices.length - onlineCount),
      syncedCount,
      playlistNames: [...new Set(outlet.devices.map((device) => device.assignedPlaylistName).filter(Boolean))],
    };
  }).sort((a, b) => a.name.localeCompare(b.name));
}

function getPrimaryAssignedPlaylistId(devices) {
  const counts = new Map();
  devices.forEach((device) => {
    if (device.assignedPlaylistId) counts.set(device.assignedPlaylistId, (counts.get(device.assignedPlaylistId) || 0) + 1);
  });
  return [...counts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] || "";
}

function clean(value) { return String(value || "").trim(); }

function formatLastSeen(value) {
  const ms = typeof value?.toMillis === "function" ? value.toMillis() : typeof value?.seconds === "number" ? value.seconds * 1000 : new Date(value || 0).getTime();
  if (!ms) return "Never connected";
  const minutes = Math.max(1, Math.floor((Date.now() - ms) / 60000));
  if (minutes < 60) return `Last seen ${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  return hours < 24 ? `Last seen ${hours}h ago` : `Last seen ${Math.floor(hours / 24)}d ago`;
}

function nextIndex(current, total) { return total > 1 ? (current + 1) % total : 0; }
function previousIndex(current, total) { return total > 1 ? (current === 0 ? total - 1 : current - 1) : 0; }
